/**
 * F39↔F41 (rev-10 follow-up) — admin "Promote a tournament" dialog.
 *
 * The FREE admin platform-promote of a sponsored tournament — the only promote
 * path for federations (no billing venue). Creates a `scope_kind='tournament'`,
 * `placement_type='sponsored_tournament'` placement landing `active`
 * immediately (gross/net = 0; no organizer billing/credit) via
 * `POST /admin/placements/promote-tournament`.
 *
 * Gated on `placement_approvals:approve` by the page (the server re-enforces +
 * rate-limits 30/h). The tournament picker lists published/registration-open
 * tournaments (the server-side eligibility — non-invite-only, not suspended —
 * is re-validated by the F41 lookup; an ineligible pick surfaces the 422).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import {
  Banner,
  Button,
  FormField,
  Modal,
  NumberInput,
  SearchInput,
  Select,
  Textarea,
  TextInput,
} from '@bravemove/ui-web';
import {
  PLACEMENT_MAX_WEEKS,
  PLACEMENT_MIN_WEEKS,
  PromoteTournamentPlacementInputSchema,
  type Placement,
  type PromoteTournamentPlacementInput,
} from '@bravemove/types';
import { adminPlacementsApi } from '../../api/placements';
import { adminTournamentsApi, type AdminTournamentRow } from '../../api/tournaments';
import { translateApiError } from '../../auth/errors';

export interface PromoteTournamentDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
  /** Called with the created (active) placement so the page can refetch + toast. */
  readonly onPromoted: (created: Placement) => void | Promise<void>;
}

/** datetime-local value → ISO-8601 (empty → undefined = "starts now"). */
function toIso(local: string): string | undefined {
  if (!local) return undefined;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

export function PromoteTournamentDialog({
  open,
  onClose,
  onPromoted,
}: PromoteTournamentDialogProps) {
  const { t } = useI18n();

  const [candidates, setCandidates] = useState<readonly AdminTournamentRow[]>([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [tournamentId, setTournamentId] = useState('');
  const [weeks, setWeeks] = useState(1);
  const [periodStart, setPeriodStart] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadCandidates = useCallback(async () => {
    setCandidatesLoading(true);
    try {
      // Eligible statuses for a sponsored promote: published / registration_open
      // (the server's F41 lookup re-validates non-invite-only + not-suspended).
      const [published, regOpen] = await Promise.all([
        adminTournamentsApi.listTournaments({
          status: 'published',
          suspended: false,
          page: 1,
          perPage: 100,
        }),
        adminTournamentsApi.listTournaments({
          status: 'registration_open',
          suspended: false,
          page: 1,
          perPage: 100,
        }),
      ]);
      setCandidates([...published.data, ...regOpen.data]);
    } catch {
      setCandidates([]);
    } finally {
      setCandidatesLoading(false);
    }
  }, []);

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!open) return;
    setSearch('');
    setTournamentId('');
    setWeeks(1);
    setPeriodStart('');
    setReason('');
    setError(null);
    void loadCandidates();
  }, [open, loadCandidates]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return candidates;
    return candidates.filter((c) => c.title.toLowerCase().includes(q));
  }, [candidates, search]);

  const payload = useMemo<PromoteTournamentPlacementInput | null>(() => {
    const start = toIso(periodStart);
    const candidate = {
      tournamentId,
      weeks,
      ...(start !== undefined ? { periodStart: start } : {}),
      reason: reason.trim(),
    };
    const parsed = PromoteTournamentPlacementInputSchema.safeParse(candidate);
    return parsed.success ? parsed.data : null;
  }, [tournamentId, weeks, periodStart, reason]);

  const submit = async (): Promise<void> => {
    if (!payload) return;
    setSubmitting(true);
    setError(null);
    try {
      const created = await adminPlacementsApi.promoteTournament(payload);
      await onPromoted(created);
      onClose();
    } catch (err) {
      setError(translateApiError(err, t));
    } finally {
      setSubmitting(false);
    }
  };

  const tournamentOptions = useMemo(
    () => [
      {
        value: '',
        label: candidatesLoading
          ? t('admin:placement.promoteDialog.loading', { defaultValue: 'Loading…' })
          : t('admin:placement.promoteDialog.pickTournament', {
              defaultValue: 'Pick a tournament',
            }),
      },
      ...visible.map((c) => ({
        value: c.id,
        label: `${c.title} · ${c.sportName.replace(/-/g, ' ')}`,
      })),
    ],
    [visible, candidatesLoading, t],
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('admin:placement.promoteDialog.title', { defaultValue: 'Promote a tournament' })}
      description={t('admin:placement.promoteDialog.body', {
        defaultValue:
          'Free platform-promote (no billing) — the tournament renders in the sponsored slot with a Sponsored badge. The only promote path for federations.',
      })}
      size="md"
      footer={
        <>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={submitting}
            data-testid="promote-tournament-cancel"
          >
            {t('admin:placement.promoteDialog.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            variant="primary"
            onClick={() => void submit()}
            loading={submitting}
            disabled={payload === null || submitting}
            data-testid="promote-tournament-submit"
          >
            {t('admin:placement.promoteDialog.confirm', { defaultValue: 'Promote' })}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3" data-testid="promote-tournament-dialog">
        {error ? <Banner tone="danger">{error}</Banner> : null}

        <FormField
          label={t('admin:placement.promoteDialog.searchLabel', {
            defaultValue: 'Search tournaments',
          })}
        >
          <SearchInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('admin:placement.promoteDialog.searchPlaceholder', {
              defaultValue: 'Filter by title',
            })}
            data-testid="promote-tournament-search"
          />
        </FormField>

        <FormField
          label={t('admin:placement.promoteDialog.tournamentLabel', {
            defaultValue: 'Tournament',
          })}
          required
        >
          <Select
            value={tournamentId}
            onChange={(e) => setTournamentId(e.target.value)}
            options={tournamentOptions}
            data-testid="promote-tournament-select"
          />
        </FormField>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <FormField
            label={t('admin:placement.promoteDialog.weeksLabel', { defaultValue: 'Weeks' })}
            required
          >
            <NumberInput
              value={weeks}
              min={PLACEMENT_MIN_WEEKS}
              max={PLACEMENT_MAX_WEEKS}
              step={1}
              onChange={(v) =>
                setWeeks(
                  Math.min(
                    PLACEMENT_MAX_WEEKS,
                    Math.max(PLACEMENT_MIN_WEEKS, Math.round(Number(v) || PLACEMENT_MIN_WEEKS)),
                  ),
                )
              }
              data-testid="promote-tournament-weeks"
              aria-label={t('admin:placement.promoteDialog.weeksLabel', { defaultValue: 'Weeks' })}
            />
          </FormField>
          <FormField
            label={t('admin:placement.promoteDialog.startLabel', {
              defaultValue: 'Starts (optional — now when empty)',
            })}
          >
            <TextInput
              type="datetime-local"
              value={periodStart}
              onChange={(e) => setPeriodStart(e.target.value)}
              data-testid="promote-tournament-start"
            />
          </FormField>
        </div>

        <FormField
          label={t('admin:placement.promoteDialog.reasonLabel', { defaultValue: 'Reason' })}
          required
        >
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('admin:placement.promoteDialog.reasonPlaceholder', {
              defaultValue: 'Why is the platform promoting this tournament? (≥10 chars)',
            })}
            rows={3}
            maxLength={500}
            data-testid="promote-tournament-reason"
          />
        </FormField>
      </div>
    </Modal>
  );
}

export default PromoteTournamentDialog;
